import { useState, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notesApi } from '@/api/notes';

export interface NoteDeleteControls {
  confirmDelete: boolean;
  setConfirmDelete: Dispatch<SetStateAction<boolean>>;
  pwdInput: string;
  setPwdInput: Dispatch<SetStateAction<string>>;
  pwdError: string | null;
  /** Se verifică parola master. */
  busy: boolean;
  /** Cererea de ștergere e în curs. */
  deleting: boolean;
  submitDelete: (e: FormEvent) => Promise<void>;
  cancelDelete: () => void;
}

/**
 * Ștergerea unei notițe, confirmată cu parola master. Hook (nu componentă cu
 * stare proprie) ca mutația să trăiască cât editorul, nu cât formularul.
 */
export function useNoteDelete({
  noteId,
  tenantId,
  verifyPassword,
  onDeleted,
  onError,
}: {
  noteId: string | null;
  tenantId: string;
  verifyPassword: (password: string) => Promise<boolean>;
  onDeleted?: () => void;
  /** Eroarea de la server ajunge în banner-ul general al editorului. */
  onError: (message: string) => void;
}): NoteDeleteControls {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pwdInput, setPwdInput] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => notesApi.delete(noteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['trash', tenantId] });
      onDeleted?.();
    },
    onError: (err) => {
      if (err instanceof Error) onError(err.message);
      else onError(t('notes_ui.editor.delete_error'));
    },
  });

  const submitDelete = async (e: FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    if (!pwdInput) {
      setPwdError(t('notes_ui.editor.enter_master_password'));
      return;
    }
    setBusy(true);
    try {
      const ok = await verifyPassword(pwdInput);
      if (!ok) {
        setPwdError(t('notes_ui.editor.wrong_password'));
        setPwdInput('');
        return;
      }
      deleteMutation.mutate();
    } catch (err) {
      setPwdError(err instanceof Error ? err.message : t('notes_ui.editor.verify_error'));
    } finally {
      setBusy(false);
    }
  };

  const cancelDelete = () => {
    setConfirmDelete(false);
    setPwdInput('');
    setPwdError(null);
  };

  return {
    confirmDelete,
    setConfirmDelete,
    pwdInput,
    setPwdInput,
    pwdError,
    busy,
    deleting: deleteMutation.isPending,
    submitDelete,
    cancelDelete,
  };
}
